import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

class ProductQuantityDropdown extends Component {
  requiredRefs = ['quantityInput', 'quantitySelect'];

  connectedCallback() {
    super.connectedCallback();

    this.#syncOptions();
    this.refs.quantitySelect.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.quantitySelect.removeEventListener('change', this.#handleChange);
  }

  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.#syncOptions();
  }

  canAddToCart() {
    const { max, cartQuantity, value } = this.#getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  getValue() {
    return this.refs.quantityInput.value;
  }

  setValue(value) {
    this.refs.quantityInput.value = value;
    this.#syncOptions();
  }

  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;

    quantityInput.min = min;
    quantityInput.dataset.min = min;

    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }

    quantityInput.step = step;
    this.#syncOptions();
  }

  #handleChange = () => {
    const value = this.refs.quantitySelect.value;

    this.refs.quantityInput.value = value;
    this.dispatchEvent(new QuantitySelectorUpdateEvent(Number(value)));
  };

  #getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min || quantityInput.dataset.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 1),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
      fallbackMax: parseIntOrDefault(this.dataset.fallbackMax, 10),
    };
  }

  #getEffectiveMax() {
    const { min, max, cartQuantity, fallbackMax } = this.#getCurrentValues();

    if (max === null) {
      return Math.max(min, fallbackMax);
    }

    return Math.max(max - cartQuantity, min);
  }

  #getValidValues() {
    const { min, step } = this.#getCurrentValues();
    const effectiveMax = this.#getEffectiveMax();
    const values = [];

    for (let value = min; value <= effectiveMax; value += step) {
      values.push(value);
    }

    if (values.length === 0) {
      values.push(min);
    }

    return values;
  }

  #syncOptions() {
    const { quantityInput, quantitySelect } = this.refs;
    const currentValue = parseIntOrDefault(quantityInput.value, 1);
    const values = this.#getValidValues();

    let nextValue = currentValue;
    if (!values.includes(nextValue)) {
      nextValue = values.filter((value) => value <= currentValue).at(-1) ?? values[0];
    }

    quantitySelect.innerHTML = values
      .map((value) => `<option value="${value}">${value}</option>`)
      .join('');

    quantitySelect.value = nextValue.toString();
    quantityInput.value = nextValue.toString();
  }
}

if (!customElements.get('product-quantity-dropdown')) {
  customElements.define('product-quantity-dropdown', ProductQuantityDropdown);
}
